import React, { useMemo, useEffect } from 'react'
import { FaChevronRight } from "react-icons/fa";
import Progress from "../../progress";
import Dish1 from "../../../../resources/imgs/assessment/dishes/dish1.svg";
import Dish2 from "../../../../resources/imgs/assessment/dishes/dish2.svg";
import Dish3 from "../../../../resources/imgs/assessment/dishes/dish3.svg";
import Dish4 from "../../../../resources/imgs/assessment/dishes/dish4.svg";
import Dish5 from "../../../../resources/imgs/assessment/dishes/dish5.svg";
import Dish6 from "../../../../resources/imgs/assessment/dishes/dish6.svg";
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { loveInterestAtom, randomGuestAtom } from '../../../../state/assessment';
import manuel1 from "../../../../resources/imgs/assessment/characters/manuel1.png";
import pamela1 from "../../../../resources/imgs/assessment/characters/pamela1.png";
import trish1 from "../../../../resources/imgs/assessment/characters/trish1.png";
import karl1 from "../../../../resources/imgs/assessment/characters/karl1.png";
import joey1 from "../../../../resources/imgs/assessment/characters/joey1.png";
import reina1 from "../../../../resources/imgs/assessment/characters/reina1.png";

const GUEST_IMAGES = {
  "Manuel": manuel1,
  "Pamela": pamela1,
  "Trish": trish1,
  "Karl": karl1,
  "Joey": joey1,
  "Reina": reina1
};

const DISH_IMAGES = {
  "Fish": Dish1,
  "Chicken": Dish2,
  "Lamb": Dish3, 
  "Steak": Dish4,
  "Vegetarian": Dish5,
  "Pasta": Dish6,
  "Salmon": Dish1 // Default to Fish image if no specific salmon image
};

const DISHES = ["Steak", "Salmon", "Pasta", "Vegetarian", "Chicken", "Lamb"];

// Use the same 6 guests as Module 3 Final Orders page
const FINAL_ORDER_GUESTS = [
  { name: "Manuel" },
  { name: "Pamela" },
  { name: "Trish" },
  { name: "Karl" },
  { name: "Joey" },
  { name: "Reina" }
];

const getGuestPronoun = (name) => {
  const femaleNames = ['Pamela', 'Trish', 'Reina'];
  return femaleNames.includes(name) ? 'she' : 'he';
};

export default function Index(props) {
  const loveInterest = useRecoilValue(loveInterestAtom);
  const setRandomGuest = useSetRecoilState(randomGuestAtom);
  
  const { randomGuest, guestDish, dishOptions, correctDishIndex } = useMemo(() => {
    if (!props.cardData) return { randomGuest: null, guestDish: null, dishOptions: [], correctDishIndex: null };
    
    // Extract love interest name from object if needed
    const loveInterestName = typeof loveInterest === 'object' ? loveInterest.loveInterest : loveInterest;
    
    console.log('🔍 Module 8-4 Debug:', { 
      loveInterest, 
      loveInterestName,
      finalOrderGuests: FINAL_ORDER_GUESTS.map(g => g.name)
    });
    
    // Get full guest data from props.cardData to access image properties
    const availableGuestNames = FINAL_ORDER_GUESTS.filter(guest => guest.name !== loveInterestName).map(g => g.name);
    const availableGuestData = props.cardData?.filter(guest => availableGuestNames.includes(guest.name)) || [];
    
    console.log('🔍 Available guests:', availableGuestNames);
    // Use deterministic selection - always pick the first available guest for consistency
    const selectedGuest = availableGuestData[0];
    console.log('🔍 Selected guest (deterministic - first available):', selectedGuest);
    
    // Find the guest's index in the Final Orders list to get their dish
    const guestIndex = FINAL_ORDER_GUESTS.findIndex(guest => guest.name === selectedGuest.name);
    const dish = DISHES[guestIndex];
    
    // Create 4 dish options, ensuring the correct dish is included
    const allDishes = ["Steak", "Salmon", "Pasta", "Vegetarian", "Chicken", "Lamb"];
    const otherDishes = allDishes.filter(d => d !== dish);
    
    // Randomly select 3 other dishes
    const shuffledOthers = [...otherDishes].sort(() => Math.random() - 0.5).slice(0, 3);
    
    // Randomly place the correct dish in one of the 4 positions
    const correctPosition = Math.floor(Math.random() * 4);
    const options = [];
    
    for (let i = 0; i < 4; i++) {
      if (i === correctPosition) {
        options.push(dish);
      } else {
        options.push(shuffledOthers[options.length - (correctPosition <= i ? 1 : 0)]);
      }
    }
    
    return {
      randomGuest: selectedGuest,
      guestDish: dish,
      dishOptions: options,
      correctDishIndex: correctPosition + 21 // Convert to props.Choose index (21-24)
    };
  }, [props.cardData, loveInterest]);
  
  // Store the random guest in the atom for other modules to use
  useEffect(() => {
    if (randomGuest) {
      console.log('🔍 Module 8-4 setting randomGuest:', randomGuest);
      setRandomGuest(randomGuest);
    }
  }, [randomGuest, setRandomGuest]);
  
  const guestPronoun = randomGuest ? getGuestPronoun(randomGuest.name) : 'they';
  
  return (
    <div className="module1-main">
      <div className="module1-wrap">
        <Progress number="08." percent={1} />
        <div className="module1-content-wrap">
          <div className="module1-logo">Module 8</div>
          <div className="module1-body">
            <div className="module1-title">Guest interrupts!</div>
            {randomGuest && (
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <img 
                  className="module1-drag-img1"
                  src={`${process.env.PUBLIC_URL}/images/characters/${randomGuest.name}/${randomGuest.name}%201.png`} 
                  alt={randomGuest.name}
                  onError={(e) => {
                    console.error(`Failed to load image for ${randomGuest.name}:`, e.target.src);
                  }}
                />
              </div>
            )}
            <div className="module1-content1">
              <span style={{ fontWeight: 'bold' }}>Guest:</span> Hey! {randomGuest?.name} is sick. {randomGuest?.name} has Celiac's disease and can't eat gluten. Do you remember what {randomGuest?.name} ordered?
            </div>
            <div className="module1-content2">
              <span style={{ fontWeight: 'bold' }}>You:</span> Oh sure. {randomGuest?.name} ordered...
            </div>
            <div className="module1-content2">
              <span style={{ fontWeight: 'bold' }}>Guest:</span> No gluten?
            </div>
            <div className="module1-content2">
              <span style={{ fontWeight: 'bold' }}>You:</span> Nope! I wish {guestPronoun} had told me!
            </div>
            <div className="module1-content2 module11-title">
              What did {randomGuest?.name} order?
            </div>
          </div>
        </div>
      </div>

      <div className="module1-categories"></div>
      <div className="module1-dishes module1-dishes2">
        {dishOptions.map((dishName, index) => (
          <div key={index}>
            <div
              className="module1-dish module6-dish dish-container module11-food-wrap"
              onClick={() => props.Choose(21 + index)}
              style={{ background: props.res[21 + index] ? "#F277C6" : "" }}
            >
              <img src={DISH_IMAGES[dishName]} alt={dishName} />
            </div>
            <div className="module11-food-item">{dishName}</div>
          </div>
        ))}
      </div>

      {props.res.length !== 0 && (
        <div className="assessment-group-btn">
          <div></div>
          <div
            className="assessment-next-btn module8-next-btn"
            onClick={() => {
              // Log the answer for tracking (no scoring impact)
              if (correctDishIndex) {
                const selectedCorrectly = props.res[correctDishIndex];
                console.log(`🍽️ Guest interrupts answer: Guest=${randomGuest?.name}, CorrectDish=${guestDish}, UserSelectedCorrectly=${!!selectedCorrectly}`);
              }
              props.nextPage();
            }}
          >
            Next{" "}
            <div
              style={{
                paddingTop: "5px",
                display: "inline-block",
              }}
            >
              <FaChevronRight color="white" size={16} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
